import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { AtheismResponse } from "./types";
import { fetchAtheismDefense, saveAtheismResponse } from "./services/atheismService";
import { shareViaEmail, shareViaSMS, shareToFacebook } from "./utils/shareUtils";

export const useAtheismDefense = () => {
  const { user } = useAuth();
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [response, setResponse] = useState<AtheismResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!question.trim()) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const responseData = await fetchAtheismDefense(question);
      
      // Log the structure of the response to help with debugging
      console.log("Received response:", responseData);
      
      // Ensure scripture references is always an array
      if (!Array.isArray(responseData.scriptureReferences)) {
        responseData.scriptureReferences = [];
      }
      
      setResponse(responseData);
    } catch (err: any) {
      console.error("Error fetching atheism defense:", err);
      setError(err.message || "We encountered an error processing your question. Please try again.");
      toast({
        title: "Error",
        description: "We encountered an error processing your question. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSampleQuestion = (sample: string) => {
    setQuestion(sample);
  };

  const saveResponse = async () => {
    if (!response || !user) {
      toast({
        title: "Cannot save response",
        description: user ? "No response to save" : "Please sign in to save responses",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSaving(true);
      await saveAtheismResponse(user.id, question, response);
    } catch (error) {
      console.error('Error saving response:', error);
      toast({
        title: "Error",
        description: "Failed to save response. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Using the sharing utility functions
  const handleShareViaEmail = () => shareViaEmail(question, response);
  const handleShareViaSMS = () => shareViaSMS(question, response);
  const handleShareToFacebook = () => shareToFacebook();

  return {
    question,
    setQuestion,
    isLoading,
    isSaving,
    response,
    error,
    handleSubmit,
    handleSampleQuestion,
    saveResponse,
    shareViaEmail: handleShareViaEmail,
    shareViaSMS: handleShareViaSMS,
    shareToFacebook: handleShareToFacebook
  };
};
